import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import { productImageUrl } from './AiLookResults';
import SocialShareButtons from './SocialShareButtons';
import TryOnShareCard from './TryOnShareCard';
import { formatProductPriceEur, selectedProductsTotalEur } from './currency';
import useModalEscape from './useModalEscape';

const TRYON_FEED_CACHE_KEY = 'od_tryon_feed_cache_v1';
const TRYON_FEED_REFRESH_EVENT = 'od:tryon-feed-refresh';

function refreshPublicTryOnFeedCache() {
  try {
    sessionStorage.removeItem(TRYON_FEED_CACHE_KEY);
  } catch {
    // Session cache is best-effort only.
  }
  window.dispatchEvent(new CustomEvent(TRYON_FEED_REFRESH_EVENT));
}

function normalizeViewerSource(value) {
  if (!value || typeof value !== 'string') return '';
  if (value.startsWith('data:image/')) return value;
  try {
    return new URL(value, window.location.origin).href;
  } catch {
    return '';
  }
}

export default function TryOnPreviews() {
  const { t, formatDate, locale } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [publicSavingId, setPublicSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [shareCardItem, setShareCardItem] = useState(null);
  const [viewer, setViewer] = useState({ open: false, slide: 0, zoom: 1, offsetX: 0, offsetY: 0, sources: [] });
  const pinchRef = useRef({ distance: 0, zoom: 1 });
  const dragRef = useRef({ active: false, pointerId: null, startX: 0, startY: 0, offsetX: 0, offsetY: 0 });
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }),
    [locale],
  );

  async function loadPreviews() {
    const response = await api.getTryOnPreviews();
    setItems(response.data || []);
  }

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    loadPreviews()
      .catch((err) => {
        if (mounted) setError(err.message);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const timer = window.setInterval(() => {
      loadPreviews().catch(() => {});
    }, 8000);

    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, []);

  function openViewer(sources, slide = 1) {
    const validSources = sources.map(normalizeViewerSource).filter(Boolean);
    if (validSources.length === 0) return;
    const safeSlide = Math.min(Math.max(Number(slide) || 1, 1), validSources.length) - 1;
    setViewer({
      open: true,
      slide: safeSlide,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      sources: validSources,
    });
  }

  function formatTryOnError(code) {
    if (!code) return '';
    const translated = t(`tryon.error.${code}`);
    return translated === `tryon.error.${code}` ? code : translated;
  }

  function closeViewer() {
    setViewer((current) => ({ ...current, open: false, zoom: 1, offsetX: 0, offsetY: 0 }));
    dragRef.current = { active: false, pointerId: null, startX: 0, startY: 0, offsetX: 0, offsetY: 0 };
  }

  useModalEscape(viewer.open, closeViewer);

  function moveViewer(direction) {
    setViewer((current) => ({
      ...current,
      slide: (current.slide + direction + current.sources.length) % current.sources.length,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
    }));
  }

  function zoomViewer(delta) {
    setViewer((current) => ({
      ...current,
      zoom: Math.min(4, Math.max(1, Number((current.zoom + delta).toFixed(2)))),
      offsetX: current.zoom + delta <= 1 ? 0 : current.offsetX,
      offsetY: current.zoom + delta <= 1 ? 0 : current.offsetY,
    }));
  }

  function setViewerZoom(nextZoom) {
    const zoom = Math.min(4, Math.max(1, Number(nextZoom.toFixed(2))));
    setViewer((current) => ({
      ...current,
      zoom,
      offsetX: zoom <= 1 ? 0 : current.offsetX,
      offsetY: zoom <= 1 ? 0 : current.offsetY,
    }));
  }

  function moveViewerPan(offsetX, offsetY) {
    setViewer((current) => ({
      ...current,
      offsetX: current.zoom <= 1 ? 0 : offsetX,
      offsetY: current.zoom <= 1 ? 0 : offsetY,
    }));
  }

  function touchDistance(touches) {
    const [first, second] = touches;
    return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
  }

  function handleViewerWheel(event) {
    event.preventDefault();
    const delta = event.deltaY < 0 ? 0.18 : -0.18;
    zoomViewer(delta);
  }

  function handleViewerTouchStart(event) {
    if (event.touches.length !== 2) return;
    dragRef.current.active = false;
    pinchRef.current = {
      distance: touchDistance(event.touches),
      zoom: viewer.zoom,
    };
  }

  function handleViewerTouchMove(event) {
    if (event.touches.length !== 2 || !pinchRef.current.distance) return;
    event.preventDefault();
    const scale = touchDistance(event.touches) / pinchRef.current.distance;
    setViewerZoom(pinchRef.current.zoom * scale);
  }

  function handleViewerTouchEnd(event) {
    if (event.touches.length < 2) {
      pinchRef.current = { distance: 0, zoom: viewer.zoom };
    }
  }

  function handleViewerPointerDown(event) {
    if (viewer.zoom <= 1 || event.button > 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: viewer.offsetX,
      offsetY: viewer.offsetY,
    };
  }

  function handleViewerPointerMove(event) {
    const drag = dragRef.current;
    if (!drag.active || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    moveViewerPan(
      drag.offsetX + event.clientX - drag.startX,
      drag.offsetY + event.clientY - drag.startY,
    );
  }

  function handleViewerPointerUp(event) {
    if (dragRef.current.pointerId === event.pointerId) {
      dragRef.current.active = false;
      dragRef.current.pointerId = null;
    }
  }

  function getTryOnShareData(item) {
    const celebrity = item.event?.celebrity_name || t('tryonShare.celebrityFallback');
    const pathname = item.public_share_id ? `/t/${item.public_share_id}` : item.event?.id ? `/events/${item.event.id}` : '/profile?tab=tryons';
    const url = new URL(pathname, window.location.origin).href;
    const isShopTryOn = item.try_on_type === 'selected_products';
    const total = formatSelectedTotal(item);
    return {
      celebrity,
      url,
      title: isShopTryOn ? t('tryonShare.shopTitle', { celebrity, total }) : t('tryonShare.title', { celebrity }),
      text: isShopTryOn ? t('tryonShare.shopText', { celebrity, total }) : t('tryonShare.text', { celebrity }),
      media: item.image_url,
    };
  }

  function formatPrice(product) {
    return formatProductPriceEur(product, currencyFormatter, t('detail.priceUnavailable'));
  }

  function formatSelectedTotal(item) {
    const total = selectedProductsTotalEur(item?.selected_products || []);
    if (total === null) return t('tryon.totalUnavailable');
    return currencyFormatter.format(total);
  }

  async function updatePublicSettings(item, nextPublic, nextUseProfileName) {
    if (item.status !== 'completed' || !item.image_url || publicSavingId) return;
    if (nextPublic && !item.is_public && !window.confirm(t('tryonPublic.publishConfirm'))) return;
    setPublicSavingId(item.id);
    setError('');

    try {
      const response = await api.updateTryOnPublic(item.id, nextPublic, {
        useProfileName: nextUseProfileName,
        publicConsent: nextPublic === true,
      });
      setItems((current) => current.map((preview) => (preview.id === item.id ? response.data : preview)));
      refreshPublicTryOnFeedCache();
    } catch (err) {
      setError(t(`tryonPublic.error.${err.message}`) === `tryonPublic.error.${err.message}` ? t('tryonPublic.error.generic') : t(`tryonPublic.error.${err.message}`));
    } finally {
      setPublicSavingId(null);
    }
  }

  function togglePublic(item) {
    updatePublicSettings(item, !item.is_public, item.public_use_profile_name);
  }

  function togglePublicName(item) {
    updatePublicSettings(item, item.is_public, !item.public_use_profile_name);
  }

  async function deletePreview(item) {
    if (deletingId) return;
    if (!window.confirm(t('tryonGallery.deleteConfirm'))) return;

    setDeletingId(item.id);
    setError('');

    try {
      await api.deleteTryOnPreview(item.id);
      setItems((current) => current.filter((preview) => preview.id !== item.id));
      if (item.is_public) refreshPublicTryOnFeedCache();
    } catch (err) {
      setError(t(`tryonGallery.error.${err.message}`) === `tryonGallery.error.${err.message}` ? t('tryonGallery.error.deleteFailed') : t(`tryonGallery.error.${err.message}`));
    } finally {
      setDeletingId(null);
    }
  }

  useEffect(() => {
    if (!viewer.open) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'ArrowLeft' && viewer.sources.length > 1) moveViewer(-1);
      if (event.key === 'ArrowRight' && viewer.sources.length > 1) moveViewer(1);
      if (event.key === '+' || event.key === '=') zoomViewer(0.25);
      if (event.key === '-') zoomViewer(-0.25);
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [viewer.open, viewer.sources.length]);

  if (loading) return <div className="ct-card ct-empty">{t('common.loading')}</div>;
  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <section className="ct-tryon-gallery">
      {shareCardItem ? (
        <TryOnShareCard
          item={shareCardItem}
          formatPrice={formatPrice}
          formatSelectedTotal={formatSelectedTotal}
          onClose={() => setShareCardItem(null)}
        />
      ) : null}
      {viewer.open ? (
        <div className="ct-photo-viewer" role="presentation" onMouseDown={closeViewer}>
          <div
            className="ct-photo-viewer-stage"
            role="dialog"
            aria-modal="true"
            aria-label={t('tryonGallery.viewerLabel')}
            onMouseDown={(event) => event.stopPropagation()}
            onWheel={handleViewerWheel}
            onTouchStart={handleViewerTouchStart}
            onTouchMove={handleViewerTouchMove}
            onTouchEnd={handleViewerTouchEnd}
            onPointerDown={handleViewerPointerDown}
            onPointerMove={handleViewerPointerMove}
            onPointerUp={handleViewerPointerUp}
            onPointerCancel={handleViewerPointerUp}
          >
            <div className="ct-photo-viewer-topbar">
              <span>{viewer.slide + 1} / {viewer.sources.length}</span>
              <div>
                <button type="button" onClick={() => zoomViewer(-0.25)} aria-label={t('tryonGallery.zoomOut')}>
                  <i className="ph ph-minus"></i>
                </button>
                <button type="button" onClick={() => zoomViewer(0.25)} aria-label={t('tryonGallery.zoomIn')}>
                  <i className="ph ph-plus"></i>
                </button>
                <button type="button" onClick={closeViewer} aria-label={t('aiDialog.close')}>
                  <i className="ph ph-x"></i>
                </button>
              </div>
            </div>
            {viewer.sources.length > 1 ? (
              <button type="button" className="ct-photo-viewer-nav prev" onClick={() => moveViewer(-1)} aria-label={t('common.previous')}>
                <i className="ph ph-arrow-left"></i>
              </button>
            ) : null}
            <img
              src={viewer.sources[viewer.slide]}
              alt=""
              className={viewer.zoom > 1 ? 'is-zoomed' : ''}
              draggable="false"
              style={{ transform: `translate(${viewer.offsetX}px, ${viewer.offsetY}px) scale(${viewer.zoom})` }}
            />
            {viewer.sources.length > 1 ? (
              <button type="button" className="ct-photo-viewer-nav next" onClick={() => moveViewer(1)} aria-label={t('common.next')}>
                <i className="ph ph-arrow-right"></i>
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {items.length === 0 ? (
        <div className="ct-card ct-empty">
          <h2>{t('tryonGallery.emptyTitle')}</h2>
          <p>{t('tryonGallery.emptyCopy')}</p>
        </div>
      ) : (
        items.map((item) => (
          <article className="ct-tryon-card" key={item.id}>
            <div className="ct-tryon-card-media">
              {item.status === 'completed' && item.image_url ? (
                <button
                  type="button"
                  className="ct-image-viewer-trigger"
                  onClick={() => openViewer([item.image_url, item.event?.image_url], 1)}
                  aria-label={t('tryonGallery.openGeneratedImage')}
                >
                  <img src={item.image_url} alt={t('tryon.resultAlt')} />
                </button>
              ) : (
                <span>
                  <i className={item.status === 'failed' ? 'ph ph-warning-circle' : 'ph ph-hourglass-medium'}></i>
                  {t(`tryonGallery.status.${item.status}`)}
                </span>
              )}
            </div>
            <div className="ct-tryon-card-body">
              <div className="ct-tryon-card-head">
                <strong>{item.event?.title || t('tryonGallery.untitled')}</strong>
                <em>
                  {item.try_on_type === 'selected_products' ? t('tryon.typeSelectedProducts') : t('tryon.typeReplica')}
                  {' · '}
                  {t(`tryonGallery.status.${item.status}`)}
                </em>
              </div>
              <div className="ct-tryon-source">
                {item.event?.image_url ? (
                  <button
                    type="button"
                    className="ct-image-viewer-trigger"
                    onClick={() => openViewer([item.image_url, item.event?.image_url], item.image_url ? 2 : 1)}
                    aria-label={t('tryonGallery.openOriginalImage')}
                  >
                    <img src={item.event.image_url} alt="" />
                  </button>
                ) : null}
                <div>
                  <span>{item.event?.celebrity_name}</span>
                  <small>{item.event?.source_name || t('tryonGallery.originalPost')}</small>
                </div>
              </div>
              <dl className="ct-tryon-meta">
                <div>
                  <dt>{t('tryonGallery.created')}</dt>
                  <dd>{formatDate(item.created_at, { dateStyle: 'medium', timeStyle: 'short' })}</dd>
                </div>
                <div>
                  <dt>{t('tryonGallery.userPhoto')}</dt>
                  <dd>{item.user_photo?.title || '-'}</dd>
                </div>
                <div>
                  <dt>{t('tryonGallery.email')}</dt>
                  <dd>{item.notify_email ? t('tryonGallery.emailOn') : t('tryonGallery.emailOff')}</dd>
                </div>
              </dl>
              {item.try_on_type === 'selected_products' && item.selected_products?.length ? (
                <div className="ct-tryon-selected-products">
                  <strong>{t('tryon.selectedProductsTitle', { total: formatSelectedTotal(item) })}</strong>
                  {item.selected_products.map((product) => {
                    const content = (
                      <>
                        {product.image_url ? <img src={productImageUrl(product.image_url)} alt="" /> : <i className="ph ph-shopping-bag"></i>}
                        <span>
                          <b>{product.name}</b>
                          <small>{product.store || product.brand || product.item_category}</small>
                        </span>
                        <em>{formatPrice(product)}</em>
                      </>
                    );
                    const href = product.affiliate_url || product.product_url;
                    return href ? (
                      <a href={href} target="_blank" rel="noreferrer" key={product.candidate_id}>
                        {content}
                      </a>
                    ) : (
                      <span key={product.candidate_id}>{content}</span>
                    );
                  })}
                </div>
              ) : null}
              {item.error_message ? <p className="ct-tryon-error">{formatTryOnError(item.error_message)}</p> : null}
              {item.status === 'completed' && item.image_url ? (
                <div className={`ct-public-toggle-card ${item.is_public ? 'is-public' : ''}`}>
                  <div>
                    <strong>{item.is_public ? t('tryonPublic.onTitle') : t('tryonPublic.offTitle')}</strong>
                    <span>{item.is_public ? t('tryonPublic.onCopy') : t('tryonPublic.offCopy')}</span>
                    <label className="ct-public-name-choice">
                      <input
                        type="checkbox"
                        checked={Boolean(item.public_use_profile_name)}
                        onChange={() => togglePublicName(item)}
                        disabled={publicSavingId === item.id}
                      />
                      <span>
                        <strong>{t('tryonPublic.useName')}</strong>
                        <em>
                          {item.public_use_profile_name
                            ? t('tryonPublic.useNameCopy')
                            : t('tryonPublic.aliasCopy', { alias: item.public_alias || t('tryonPublic.aliasPending') })}
                        </em>
                      </span>
                    </label>
                  </div>
                  <button
                    type="button"
                    className="ct-switch-button"
                    aria-pressed={item.is_public}
                    onClick={() => togglePublic(item)}
                    disabled={publicSavingId === item.id}
                  >
                    <span></span>
                    <em>{item.is_public ? t('common.on') : t('common.off')}</em>
                  </button>
                </div>
              ) : null}
              <div className="ct-tryon-actions">
                {item.status === 'completed' && item.image_url ? (
                  <>
                    <SocialShareButtons
                      {...getTryOnShareData(item)}
                      label={t('tryonShare.button', { celebrity: item.event?.celebrity_name || t('tryonShare.celebrityFallback') })}
                    />
                    <button
                      type="button"
                      className="ct-share-card-trigger"
                      onClick={() => setShareCardItem(item)}
                    >
                      <i className="ph ph-image-square"></i>
                      {t('shareCard.openButton')}
                    </button>
                  </>
                ) : null}
                <div className="ct-tryon-secondary-actions">
                  {item.event?.id ? (
                    <Link to={`/events/${item.event.id}`} className="ct-detail-ai-button secondary">
                      {t('tryonGallery.openEvent')}
                    </Link>
                  ) : null}
                  <button
                    type="button"
                    className="ct-detail-ai-button danger"
                    onClick={() => deletePreview(item)}
                    disabled={deletingId === item.id}
                  >
                    <i className="ph ph-trash"></i>
                    {deletingId === item.id ? t('tryonGallery.deleting') : t('tryonGallery.delete')}
                  </button>
                </div>
              </div>
            </div>
          </article>
        ))
      )}
    </section>
  );
}
